import type { VoiceSalesCall, VoiceSalesCampaign } from '../../common/api/voiceSales';

export type SortDirection = 'asc' | 'desc';
export type CallSortingField = 'started' | 'duration' | 'status' | 'campaign';
export type CampaignSortingField = 'campaign' | 'status' | 'dailyLimit' | 'created';

export type CallsCollectionState = {
  q: string;
  campaign: string;
  status: string;
  sortingField: CallSortingField;
  sortDirection: SortDirection;
};

export type CampaignsCollectionState = {
  q: string;
  status: string;
  mode: string;
  sortingField: CampaignSortingField;
  sortDirection: SortDirection;
};

function normalizedSearch(value: string | null | undefined): string {
  return (value ?? '').toLocaleLowerCase().replace(/[^a-z0-9]+/g, '');
}

function compareValues(left: string | number, right: string | number, direction: SortDirection): number {
  const comparison = typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'desc' ? -comparison : comparison;
}

export function filterAndSortCalls(
  calls: VoiceSalesCall[],
  campaigns: VoiceSalesCampaign[],
  state: CallsCollectionState,
): VoiceSalesCall[] {
  const campaignNames = new Map(campaigns.map((campaign) => [campaign.id, campaign.name]));
  const query = normalizedSearch(state.q);
  const filtered = calls.filter((call) => {
    const searchText = normalizedSearch(
      [
        call.display_phone_number,
        call.campaign?.name,
        campaignNames.get(call.campaign_id),
        call.status,
        call.outcome,
        call.context?.contact?.name,
        call.context?.contact?.title,
        call.context?.contact?.department,
        call.context?.business?.name,
        call.context?.business?.address,
        call.context?.scorecard?.business_type,
        ...(call.recent_actions ?? []).map((action) => action.summary),
      ].filter(Boolean).join(' '),
    );
    return (
      (!query || searchText.includes(query)) &&
      (!state.campaign || String(call.campaign_id) === state.campaign) &&
      (!state.status || call.status === state.status)
    );
  });

  return [...filtered].sort((left, right) => {
    if (state.sortingField === 'duration') {
      return compareValues(left.duration_seconds ?? -1, right.duration_seconds ?? -1, state.sortDirection);
    }
    if (state.sortingField === 'status') {
      return compareValues(left.status, right.status, state.sortDirection);
    }
    if (state.sortingField === 'campaign') {
      return compareValues(
        campaignNames.get(left.campaign_id) ?? '',
        campaignNames.get(right.campaign_id) ?? '',
        state.sortDirection,
      );
    }
    return compareValues(
      left.started_at ? new Date(left.started_at).getTime() : 0,
      right.started_at ? new Date(right.started_at).getTime() : 0,
      state.sortDirection,
    );
  });
}

export function filterAndSortCampaigns(
  campaigns: VoiceSalesCampaign[],
  state: CampaignsCollectionState,
): VoiceSalesCampaign[] {
  const query = normalizedSearch(state.q);
  const filtered = campaigns.filter((campaign) => {
    const searchText = normalizedSearch(
      [campaign.name, campaign.source_region, campaign.status, campaign.mode].join(' '),
    );
    return (
      (!query || searchText.includes(query)) &&
      (!state.status || campaign.status === state.status) &&
      (!state.mode || campaign.mode === state.mode)
    );
  });

  return [...filtered].sort((left, right) => {
    if (state.sortingField === 'status') {
      return compareValues(left.status, right.status, state.sortDirection);
    }
    if (state.sortingField === 'dailyLimit') {
      return compareValues(left.daily_cap, right.daily_cap, state.sortDirection);
    }
    if (state.sortingField === 'created') {
      return compareValues(
        new Date(left.created_at).getTime(),
        new Date(right.created_at).getTime(),
        state.sortDirection,
      );
    }
    return compareValues(left.name, right.name, state.sortDirection);
  });
}

export function paginateItems<T>(items: T[], requestedPage: number, pageSize: number) {
  const pagesCount = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(Math.max(1, requestedPage), pagesCount);
  const start = (currentPage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    pagesCount,
    currentPage,
  };
}
